import React, { useState } from "react";
import { Loader2, Search, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGetContactsQuery } from "@/store/rest_api";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { getInitials, getContactDisplayName } from "@/util/string";
import { ContactType } from "@/enum/ContactType";
import { cn } from "@/lib/utils";

interface LeadPickerProps {
  selectedIds: Set<string>;
  onSelectedIdsChange: (selectedIds: Set<string>) => void;
  alreadyAddedIds?: Set<string>;
}

type LeadPickerTab = "LEADS" | "CONTACTS";

const PICKER_PAGE_SIZE = 100;

const SEARCH_DELAY_MS = 300;

export default function LeadPicker({ selectedIds, onSelectedIdsChange, alreadyAddedIds }: LeadPickerProps) {
  const [activeTab, setActiveTab] = useState<LeadPickerTab>("LEADS");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DELAY_MS);
  const { data: resultsPage, isFetching } = useGetContactsQuery({
    size: PICKER_PAGE_SIZE,
    q: debouncedSearch || undefined,
    type: activeTab === "LEADS" ? ContactType.LEAD : undefined,
    excludeType: activeTab === "CONTACTS" ? ContactType.LEAD : undefined,
  });

  const results = resultsPage?.items ?? [];
  const totalResults = resultsPage?.totalElements ?? 0;
  const selectableResults = results.filter((result) => !alreadyAddedIds?.has(result.id));
  const allVisibleSelected =
    selectableResults.length > 0 && selectableResults.every((result) => selectedIds.has(result.id));
  const recordLabel = activeTab === "LEADS" ? "leads" : "contacts";

  const toggleRecord = (recordId: string) => {
    const nextSelectedIds = new Set(selectedIds);
    if (nextSelectedIds.has(recordId)) {
      nextSelectedIds.delete(recordId);
    } else {
      nextSelectedIds.add(recordId);
    }
    onSelectedIdsChange(nextSelectedIds);
  };

  const toggleAllVisible = () => {
    const nextSelectedIds = new Set(selectedIds);
    selectableResults.forEach((result) => {
      if (allVisibleSelected) {
        nextSelectedIds.delete(result.id);
      } else {
        nextSelectedIds.add(result.id);
      }
    });
    onSelectedIdsChange(nextSelectedIds);
  };

  const isFromCheckbox = (event: React.MouseEvent<HTMLDivElement>) => {
    return event.target instanceof Element && event.target.closest("button, input") !== null;
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as LeadPickerTab)}>
          <TabsList>
            <TabsTrigger value="LEADS">Leads</TabsTrigger>
            <TabsTrigger value="CONTACTS">Contacts</TabsTrigger>
          </TabsList>
        </Tabs>
        <span className="text-sm text-muted-foreground tabular-nums">{selectedIds.size} selected</span>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
            }
          }}
          placeholder={`Search ${recordLabel} by name, company, phone or email`}
          className="pl-9"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
        {isFetching && results.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : results.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-muted-foreground">No {recordLabel} found</p>
          </div>
        ) : (
          <div>
            <div
              className="flex cursor-pointer items-center gap-3 border-b bg-muted/50 px-3 py-2 hover:bg-muted"
              onClick={(event) => {
                if (!isFromCheckbox(event)) {
                  toggleAllVisible();
                }
              }}
            >
              <Checkbox
                checked={allVisibleSelected}
                disabled={selectableResults.length === 0}
                onCheckedChange={toggleAllVisible}
              />
              <span className="text-sm font-medium tabular-nums">Select all shown ({selectableResults.length})</span>
            </div>
            {results.map((record) => {
              const initials = getInitials(record.firstName, record.lastName);
              const isAlreadyAdded = alreadyAddedIds?.has(record.id) ?? false;
              return (
                <div
                  key={record.id}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2",
                    isAlreadyAdded ? "cursor-default opacity-60" : "cursor-pointer hover:bg-muted/50",
                  )}
                  onClick={(event) => {
                    if (!isAlreadyAdded && !isFromCheckbox(event)) {
                      toggleRecord(record.id);
                    }
                  }}
                >
                  <Checkbox
                    checked={isAlreadyAdded || selectedIds.has(record.id)}
                    disabled={isAlreadyAdded}
                    onCheckedChange={() => toggleRecord(record.id)}
                  />
                  <Avatar className="h-8 w-8">
                    {record.photo && <AvatarImage src={record.photo} alt={getContactDisplayName(record) ?? undefined} />}
                    <AvatarFallback className="text-xs">
                      {initials !== "?" ? initials : <User className="h-4 w-4" />}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{getContactDisplayName(record) ?? "Unknown"}</p>
                    {(record.company || record.emails?.[0]?.email) && (
                      <p className="truncate text-xs text-muted-foreground">
                        {[record.company, record.emails?.[0]?.email].filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                  {isAlreadyAdded && <span className="shrink-0 text-xs text-muted-foreground">In campaign</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {totalResults > results.length && (
        <p className="text-xs text-muted-foreground tabular-nums">
          Showing the first {results.length} of {totalResults} {recordLabel}. Search to find the rest.
        </p>
      )}
    </div>
  );
}
